import { useMemo } from 'react'
import { AlertTriangle, Wrench } from 'lucide-react'
import {
  formatCaptureDestinationLabel,
  useCaptureFlow,
  type PresentRemedyFlowPayload,
} from '@/components/chat/capture-flow-context'
import { BrowserFlowToolCard } from './BrowserFlowToolCard'

interface PresentRemedyFlowToolMessageProps {
  part: any
}

function readPayload(part: any): PresentRemedyFlowPayload | null {
  const tryParse = (raw: unknown) => {
    if (raw == null) return null
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw)
      } catch {
        return null
      }
    }
    return raw as Record<string, unknown>
  }

  const candidate =
    tryParse(part?.output) ??
    tryParse(part?.toolInvocation?.result) ??
    tryParse(part?.input) ??
    tryParse(part?.toolInvocation?.args)

  if (!candidate || typeof candidate !== 'object') return null
  const data = candidate as Record<string, any>
  if (
    typeof data.rationale !== 'string' ||
    typeof data.title !== 'string' ||
    typeof data.entryUrl !== 'string' ||
    typeof data.riskWarning !== 'string' ||
    !Array.isArray(data.steps)
  ) {
    return null
  }

  const testId = typeof data.testId === 'string' ? data.testId : undefined
  const remediationTicketId =
    typeof data.remediationTicketId === 'string'
      ? data.remediationTicketId
      : undefined
  if (!testId && !remediationTicketId) return null

  return {
    rationale: data.rationale,
    testId,
    remediationTicketId,
    title: data.title,
    entryUrl: data.entryUrl,
    riskWarning: data.riskWarning,
    steps: data.steps,
    fallbackInstructions:
      typeof data.fallbackInstructions === 'string'
        ? data.fallbackInstructions
        : '',
  }
}

export function PresentRemedyFlowToolMessage({
  part,
}: PresentRemedyFlowToolMessageProps) {
  const flow = useCaptureFlow()
  const payload = useMemo(() => readPayload(part), [part])

  const toolCallId: string =
    part?.toolCallId ?? part?.toolInvocation?.toolCallId ?? ''

  const destinationLabel = useMemo(() => {
    if (!payload?.testId || !flow) return null
    return formatCaptureDestinationLabel({
      flowTestId: payload.testId,
      contextTestId: flow.captureContext.testId,
      taskCode: flow.captureContext.taskCode,
      testTitle: flow.captureContext.testTitle,
      controlRefs: flow.captureContext.controlRefs,
    })
  }, [payload, flow])

  if (!payload) return null

  const extensionInstalled = flow?.extensionInstalled ?? null
  const captureStartingId = flow?.captureStartingId ?? null
  const remedyStartingId = flow?.remedyStartingId ?? null
  const isStarting = remedyStartingId === toolCallId
  const disabled = !flow || !!captureStartingId || !!remedyStartingId

  return (
    <BrowserFlowToolCard
      title='Remedy in browser'
      rationale={payload.rationale}
      icon={Wrench}
      destinationLabel={
        destinationLabel ? (
          <>
            This remedy is related to{' '}
            <span className='font-semibold'>{destinationLabel}</span>.
          </>
        ) : payload.remediationTicketId ? (
          'This remedy is related to the selected finding.'
        ) : (
          'This remedy is related to the current test.'
        )
      }
      entryUrl={payload.entryUrl}
      fallbackInstructions={payload.fallbackInstructions}
      showFallback={extensionInstalled === false}
      warning={
        <div className='rounded-md border border-status-amber-border bg-status-amber-subtle px-3 py-2'>
          <div className='flex items-start gap-2'>
            <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0 text-status-amber-emphasis' />
            <div>
              <div className='text-[10px] font-semibold tracking-[0.08em] text-status-amber-strong uppercase'>
                Live setting
              </div>
              <div className='mt-1 text-[11px] leading-[1.45] text-text-body'>
                {payload.riskWarning}
              </div>
            </div>
          </div>
        </div>
      }
      steps={payload.steps}
      buttonIcon={Wrench}
      buttonLabel='Remedy in Browser'
      loadingLabel='Opening remedy...'
      installLabel='Install Screenata Capture'
      isLoading={isStarting}
      disabled={disabled}
      extensionInstalled={extensionInstalled}
      onClick={() => flow?.startRemedy(payload, toolCallId)}
    />
  )
}
